import React, { memo, useState } from 'react';
import { Button, Modal } from '@gerdau/hefesto';
import { Tag } from '@gerdau/hefesto-mais-web';

import {
  CardContainer,
  PriceAgreementContainer,
  CodeInfo,
  ProductInfo,
  ProductPrice,
  AdditionalInfo,
  ButtonsContainer,
  BaseContainer
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  CartItemPayload,
  SortProductsItem
} from '../../../../../services/MVP/models';
import { ApplicationState } from '../../../../../store';
import { useCart } from '../useCart';
import { useCartService } from '../../../../../services/MVP/useCartService';
import { useShoppingCart } from '../../../../../providers/shoppingcart.provider';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import {
  removeCrossSellItem,
  addCrossSellSku
} from '../../../../../store/ducks/cross-sell/actions';
import { useSelectEmitter } from '../../../../../services/MVP/useSelectEmitter';
import { currencyFormatter } from '../../CheckoutMVP/utils/formatterHelper';
import { v4 as uuidv4 } from 'uuid';
import { GetFlag } from '../../../../../utils/featureFlag';
import {
  setTipoCompraForSku,
  setLastTipoCompra
} from '../../../../../services/MVP/tipoCompraMap';
import { CrossSellContext } from '../../../../../utils/cross-sell';

interface ProductCardProps {
  index: number;
  product?: SortProductsItem;
  setLoading: (loading: boolean) => void;
  setCrossSellKey: React.Dispatch<React.SetStateAction<string>>;
  orderSoldTo?: string;
  orderShipTo?: string;
  screenName?: string;
  onItemAdded?: () => void;
  syncLoading?: boolean;
  crossSellContext?: CrossSellContext;
}

const AgreementPriceChip = ({ agreedPrice }: { agreedPrice?: boolean }) => {
  if (!agreedPrice) {
    return (
      <PriceAgreementContainer>
        <Tag variant="light">Preço original</Tag>
      </PriceAgreementContainer>
    );
  }

  return (
    <PriceAgreementContainer>
      <Tag variant="success">Preço acordado</Tag>
    </PriceAgreementContainer>
  );
};

const ProductCard = ({
  index,
  product,
  setLoading,
  setCrossSellKey,
  orderSoldTo,
  orderShipTo,
  screenName,
  onItemAdded,
  syncLoading,
  crossSellContext = 'checkout'
}: ProductCardProps) => {
  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity || 0
  );

  const newCartEndpoints = GetFlag('new_cart_endpoints');

  const { addItemToCart, deleteCartId } = useCart();
  const { createNewCart, getCartId, deactivateCart, addCartItem } =
    useCartService();
  const { selectEmitter } = useSelectEmitter();
  const { cart } = useShoppingCart();

  const dispatch = useDispatch();

  const [showBpModal, setShowBpModal] = useState(false);

  if (!product) {
    return null;
  }

  const agreedPrice = product.price?.isAgreementPrice?.length > 0;

  const formattedUnitPrice = currencyFormatter({
    amount: agreedPrice
      ? Number(product.price.salesAgreementData.salesAgreement)
      : Number(product.price.creditPrice),
    currency: 'BRL'
  });

  const existingPaymentType = newCartEndpoints
    ? cart?.[0]?.extension_attributes?.payment_type_gerdau
    : false;

  const samePaymentType = existingPaymentType === product.price.paymentTerms;

  const handleAddProduct = async () => {
    if (orderSoldTo && orderShipTo) {
      if (!shoppingCartState.emitterId) {
        return handleClearAndAdd(orderSoldTo, orderShipTo);
      }
      const isMismatch =
        shoppingCartState.emitterId !== orderSoldTo ||
        shoppingCartState.receiverId !== orderShipTo;
      if (isMismatch) {
        if (cartQuantity === 0) {
          return handleClearAndAdd(orderSoldTo, orderShipTo);
        }
        dispatch(
          sendTagging({
            event: 'crosssell_cart_conflict_modal_shown',
            block_id: 'cross_sell_order_detail',
            offer_issuer_cnpj: orderSoldTo,
            offer_receiver_cnpj: orderShipTo,
            current_cart_issuer_cnpj: shoppingCartState.emitterId,
            current_cart_receiver_cnpj: shoppingCartState.receiverId,
            page_location: screenName ?? 'Detalhes_do_pedido',
            screenName: screenName ?? 'Detalhes_do_pedido'
          })
        );
        setShowBpModal(true);
        return null;
      }
    }

    if (!product.price || Number(product.price.creditPrice) <= 0) {
      return null;
    }

    if (
      (!orderSoldTo || !orderShipTo) &&
      (!shoppingCartState.emitterId || !shoppingCartState.receiverId)
    ) {
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Não foi possível adicionar o produto ao carrinho.',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
      return null;
    }

    try {
      setLoading(true);
      const existingCart = getCartId(shoppingCartState.emitterId);
      let cart;
      let currentQuantity;

      if (!existingCart || existingCart.length === 0) {
        const newCart = await createNewCart(
          shoppingCartState.emitterId,
          shoppingCartState.receiverId,
          true
        );
        cart = newCart?.cartId;
        currentQuantity = 1;
      } else {
        cart = existingCart;
        currentQuantity = cartQuantity + 1;
      }

      if (!cart || cart.length === 0) {
        throw new Error();
      }

      if (currentQuantity > 30) {
        return dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Seu carrinho atingiu o limite de 30 produtos.',
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
      }

      const formattedProduct = {
        sku: product.sku,
        qty: 1,
        quote_id: cart,
        extension_attributes: {
          measure_unity_gerdau: product.price.um,
          destination_gerdau: product.price.destination,
          payment_type_gerdau:
            existingPaymentType || product.price.paymentTerms,
          has_agreed_price_gerdau: product.price.isAgreementPrice.length !== 0,
          ...(product.price.salesAgreementData.expirationDate && {
            price_agreement_date_gerdau:
              product.price.salesAgreementData.expirationDate
          }),
          sap_fields: {
            o_header_sap: [],
            o_item_sap: [],
            o_price_sap: []
          },
          cross_sell_context: crossSellContext
        }
      } as CartItemPayload;

      const addItem = await addItemToCart(formattedProduct, currentQuantity, {
        withOptions: false,
        withPrice: false
      });

      if (addItem.item_id) {
        const effectiveScreen = screenName ?? 'visualizacao_carrinho';
        dispatch(
          sendTagging({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'BRL',
              value: `${product.price.creditPrice}`,
              screenName: effectiveScreen,
              tipo_compra: 'cross_sell',
              items: [
                {
                  screenName: effectiveScreen,
                  tipo_compra: 'cross_sell',
                  item_id: `${product.sku}`,
                  item_name: `${product.name}`,
                  price: `${product.price.creditPrice}`,
                  unidade_de_medida: `${product.price.um}`,
                  quantity: product.price.quantity ?? '1',
                  item_category: `${product.type_id}`,
                  cod_gerdau: `${product.sku}`,
                  preco_acordado: product.price.isAgreementPrice.length > 0,
                  index
                }
              ]
            }
          })
        );

        setTipoCompraForSku(String(product.sku), 'cross_sell');
        setLastTipoCompra('cross_sell');

        dispatch(addCrossSellSku(product.sku, effectiveScreen));
        setCrossSellKey(uuidv4());

        if (orderSoldTo && orderShipTo) {
          dispatch(removeCrossSellItem(product.sku));
          onItemAdded?.();
        }

        dispatch(
          addToast({
            message: `Produto adicionado ao carrinho.`,
            type: EToastTypes.SUCCESS,
            durationInMS: 5000,
            spaceToTop: 77,
            labelActionOne: 'Fechar'
          })
        );

        if (!samePaymentType && newCartEndpoints && existingPaymentType) {
          dispatch(
            addToast({
              message:
                'A condição de pagamento do produto foi atualizada para a do carrinho e os preços foram recalculados.',
              type: EToastTypes.WARNING,
              durationInMS: 5000,
              spaceToTop: 77,
              labelActionOne: 'Fechar'
            })
          );
        }

        return;
      }

      throw new Error();
    } catch (error) {
      console.error(error);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Não foi possível adicionar o produto ao carrinho.',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearAndAdd = async (
    targetSoldTo?: string,
    targetShipTo?: string
  ) => {
    const soldTo = targetSoldTo ?? orderSoldTo;
    const shipTo = targetShipTo ?? orderShipTo;
    if (!soldTo || !shipTo) return;
    try {
      setShowBpModal(false);
      setLoading(true);
      if (newCartEndpoints) {
        await deactivateCart();
      }
      deleteCartId();
      const newCart = await createNewCart(soldTo, shipTo, true);
      if (!newCart?.cartId) throw new Error();
      const formattedProduct = {
        sku: product.sku,
        qty: 1,
        quote_id: newCart.cartId,
        extension_attributes: {
          measure_unity_gerdau: product.price.um,
          destination_gerdau: product.price.destination,
          payment_type_gerdau: product.price.paymentTerms,
          has_agreed_price_gerdau: product.price.isAgreementPrice.length !== 0,
          ...(product.price.salesAgreementData.expirationDate && {
            price_agreement_date_gerdau:
              product.price.salesAgreementData.expirationDate
          }),
          sap_fields: { o_header_sap: [], o_item_sap: [], o_price_sap: [] },
          cross_sell_context: crossSellContext
        }
      } as CartItemPayload;
      const addItem = await addCartItem(
        formattedProduct,
        1,
        { withOptions: false, withPrice: false },
        soldTo,
        shipTo
      );
      if (addItem?.item_id) {
        const effectiveScreen = screenName ?? 'visualizacao_carrinho';
        dispatch(
          sendTagging({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'BRL',
              value: `${product.price.creditPrice}`,
              screenName: effectiveScreen,
              tipo_compra: 'cross_sell',
              items: [
                {
                  screenName: effectiveScreen,
                  tipo_compra: 'cross_sell',
                  item_id: `${product.sku}`,
                  item_name: `${product.name}`,
                  price: `${product.price.creditPrice}`,
                  unidade_de_medida: `${product.price.um}`,
                  quantity: product.price.quantity ?? '1',
                  item_category: `${product.type_id}`,
                  cod_gerdau: `${product.sku}`,
                  preco_acordado: product.price.isAgreementPrice.length > 0,
                  index
                }
              ]
            }
          })
        );

        setTipoCompraForSku(String(product.sku), 'cross_sell');
        setLastTipoCompra('cross_sell');

        dispatch(addCrossSellSku(product.sku, effectiveScreen));
        setCrossSellKey(uuidv4());

        dispatch(removeCrossSellItem(product.sku));
        onItemAdded?.();

        await selectEmitter(soldTo, shipTo);

        dispatch(
          addToast({
            message: 'Produto adicionado ao carrinho.',
            type: EToastTypes.SUCCESS,
            durationInMS: 5000,
            spaceToTop: 77,
            labelActionOne: 'Fechar'
          })
        );
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error(error);
      dispatch(
        addToast({
          type: EToastTypes.DANGER,
          message: 'Não foi possível adicionar o produto ao carrinho.',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const productWithoutPrice =
    product.price?.creditPrice === '' ||
    !product.price?.creditPrice ||
    Number(product.price?.creditPrice) <= 0;

  if (productWithoutPrice) {
    return null;
  }

  return (
    <CardContainer className="product-cards">
      {showBpModal && (
        <Modal
          title="Seu carrinho contém produtos para outro CNPJ"
          buttons={{
            left: {
              name: 'Manter',
              children: 'Manter',
              className:
                'hefesto-button hefesto-button--secondary hefesto-button__icon--left hefesto-button__label--right  cross_sell_conflict_left_button',
              onClick: () => {
                dispatch(
                  sendTagging({
                    event: 'crosssell_cart_conflict_resolved',
                    resolution: 'manter',
                    offer_issuer_cnpj: orderSoldTo,
                    current_cart_issuer_cnpj: shoppingCartState.emitterId,
                    num_items_cleared: 0,
                    screenName: screenName ?? 'Detalhes_do_pedido'
                  })
                );
                setShowBpModal(false);
              }
            },
            right: {
              name: 'Limpar o carrinho e adicionar',
              children: 'Limpar o carrinho e adicionar',
              variant: 'danger',
              className:
                'hefesto-button hefesto-button--danger hefesto-button__icon--left hefesto-button__label--right hefesto-button--full-width cross_sell_conflict_right_button',
              onClick: () => {
                dispatch(
                  sendTagging({
                    event: 'crosssell_cart_conflict_resolved',
                    resolution: 'limpar_e_adicionar',
                    offer_issuer_cnpj: orderSoldTo,
                    current_cart_issuer_cnpj: shoppingCartState.emitterId,
                    num_items_cleared: cartQuantity,
                    screenName: screenName ?? 'Detalhes_do_pedido'
                  })
                );
                handleClearAndAdd();
              }
            }
          }}
          modalSize="sm"
        >
          Deseja limpar o carrinho e adicionar este produto?
        </Modal>
      )}
      <AgreementPriceChip agreedPrice={agreedPrice} />
      <CodeInfo>Cód. Gerdau: {product.sku}</CodeInfo>
      <ProductInfo>{product.name}</ProductInfo>
      <BaseContainer>
        <ProductPrice>
          {formattedUnitPrice}
          <span style={{ fontSize: '0.88rem', fontWeight: '400' }}>
            /{product.price.um}
          </span>
        </ProductPrice>
        {crossSellContext !== 'checkout' && (
          <div>
            <AdditionalInfo
              title={`Preço/${
                agreedPrice
                  ? product.price.salesAgreementData.agreementUm
                  : product.price.um
              }: ${formattedUnitPrice}`}
            >
              Preço/
              {agreedPrice
                ? product.price.salesAgreementData.agreementUm
                : product.price.um}
              : {formattedUnitPrice}
            </AdditionalInfo>
            {(agreedPrice
              ? product.price.salesAgreementData.paymentTermsDescription
              : product.price.paymentTermsDescription) && (
              <AdditionalInfo
                title={`Pagamento: ${
                  agreedPrice
                    ? product.price.salesAgreementData.paymentTermsDescription
                    : product.price.paymentTermsDescription
                }`}
              >
                Pagamento:{' '}
                {agreedPrice
                  ? product.price.salesAgreementData.paymentTermsDescription
                  : product.price.paymentTermsDescription}
              </AdditionalInfo>
            )}
            <AdditionalInfo
              title={`Destinação: ${
                agreedPrice
                  ? product.price.salesAgreementData.destination
                  : product.price.destination
              }`}
            >
              Destinação:{' '}
              {agreedPrice
                ? product.price.salesAgreementData.destinationDescription
                : product.price.destinationDescription}
            </AdditionalInfo>
          </div>
        )}
        <ButtonsContainer>
          <Button
            onClick={handleAddProduct}
            name="button-card"
            variant="secondary"
            loading={!!syncLoading}
            disabled={!!syncLoading}
          >
            Adicionar ao carrinho
          </Button>
        </ButtonsContainer>
      </BaseContainer>
    </CardContainer>
  );
};

export default memo(ProductCard);
